import * as React from 'react';
import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { openConfirmModal } from '@mantine/modals';
import { useQuery, useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

// API
import { GetManifestOrganizationById, PatchOrganization, PostOrganization, queryClient } from '../../api';

// Components
import { FormScaffolding, FormHandle } from '../Form/FormScaffolding';
import { hydrateManifestSection } from '../Form/helpers/hydrateManifestSection';
import { Spinner } from '../Spinner';

// Contexts
import { RefetchSelfContext } from '../../contexts/RefetchSelf';

// Types
import { IFormSection, IManifestFieldHydrated } from '../../manifests/types';
import { IManifest, IOrganization } from '../../types';

interface ClientFormProps {
    clientId?: string;
    isNew?: boolean;
    client?: IManifest;
    onCancel?: () => void;
    onSave?: () => void;
    manifestSections?: IFormSection<IManifestFieldHydrated>[];
    ancillaryData?: Record<string, any>;
}

export const defaultClientFormSections = [
    {
        manifest: hydrateManifestSection([
            { id: 'ORG0002' },
            { id: 'ORG0003', gridSpan: 12 },
            { id: 'ORG0015' },
            { id: 'ORG0001' },
            { id: 'ORG0016' },
            {},
            { id: 'ORG0007' },
            { id: 'ORG0006' },
            { id: 'ORG0009' },
            { id: 'ORG0004' },
            {},
            { id: 'ORG0005' },
            // { id: 'ORG0008' },
            { id: 'ORG0011' },
            // { id: 'ORG0012' },
            {},
            { id: 'ORG0014' },
            { id: 'ORG0013' },
            { id: 'ORG0010' }
        ])
    }
] as IFormSection<IManifestFieldHydrated>[];

export type ClientFormHandle = {
    onCancel: () => void;
};

export const ClientForm = React.forwardRef<ClientFormHandle, ClientFormProps>(
    (
        { manifestSections = defaultClientFormSections, clientId, client, onCancel, ancillaryData, onSave, isNew },
        ref
    ) => {
        const { isInitialLoading, isLoading, data, isFetching } = useQuery({
            queryKey: ['clientById', clientId],
            queryFn: async () => {
                const response = await GetManifestOrganizationById({
                    orgId: clientId ?? '',
                    manifest: manifestSections[0]?.manifest?.map(({ id }) => id).filter(Boolean)
                });

                return response;
            },
            enabled: !client,
            refetchOnWindowFocus: false
        });

        return (
            <ClientFormInner
                ref={ref}
                loading={!client && (isInitialLoading || isLoading || isFetching || !data)}
                client={data?.keyed || client}
                clientId={clientId}
                manifestSections={manifestSections}
                ancillaryData={data?.ancillary || ancillaryData}
                onCancel={onCancel}
                onSave={onSave}
                isNew={isNew}
            />
        );
    }
);

interface ClientFormInnerProps extends ClientFormProps {
    client?: IManifest;
    loading?: boolean;
    isNew?: boolean;
}

export const ClientFormInner = React.forwardRef<ClientFormHandle, ClientFormInnerProps>(
    (
        {
            client,
            manifestSections = defaultClientFormSections,
            loading,
            onCancel,
            onSave,
            clientId,
            isNew,
            ancillaryData = {}
        },
        ref
    ) => {
        const FormRef = React.useRef<FormHandle>(null);
        const { refetch } = React.useContext(RefetchSelfContext);
        const { t } = useTranslation();
        const openDiscardChangesModal = React.useCallback(({ confirmCallback }: { confirmCallback: () => void }) => {
            openConfirmModal({
                title: t('modals.confirmDiscardChanges.title', { ns: 'components' }),
                children: <Text size="sm">{t('modals.confirmDiscardChanges.body', { ns: 'components' })}</Text>,
                labels: {
                    confirm: t('modals.confirmDiscardChanges.confirmCTA', { ns: 'components' }),
                    cancel: t('cancel', { ns: 'common' })
                },
                confirmProps: { color: 'red' },
                onCancel: () => console.log('Cancel'),
                onConfirm: () => {
                    confirmCallback();
                }
            });
        }, []);
        const patchClient = useMutation(PatchOrganization, {
            onSuccess: async (data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['clientById', clientId],
                    exact: true,
                    refetchType: 'active'
                });
                queryClient.invalidateQueries({
                    queryKey: ['clientRecordById', clientId],
                    exact: true,
                    refetchType: 'active'
                });
                refetch?.();
                notifications.show({
                    title: t('updatedClient.title', {
                        ns: 'notifications'
                    }),
                    message: t('updatedClient.message', {
                        ns: 'notifications'
                    })
                });
                onSave?.();
            },
            onError: (err: any) => {
                if (err?.error?.message === 'Error: Email currently in use by another user') {
                    notifications.show({
                        color: 'red',
                        title: t('primaryContactEmailAlreadyInUser.title', {
                            ns: 'notifications'
                        }),
                        message: t('primaryContactEmailAlreadyInUser.message', {
                            ns: 'notifications'
                        })
                    });
                } else {
                    notifications.show({
                        color: 'red',
                        title: t('errorUpdatingClient.title', {
                            ns: 'notifications'
                        }),
                        message: t('errorUpdatingClient.message', {
                            ns: 'notifications'
                        })
                    });
                }
            }
        });
        const postClient = useMutation(PostOrganization, {
            onSuccess: async (data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['clientRecords'],
                    exact: false,
                    refetchType: 'active'
                });
                notifications.show({
                    title: t('createdClient.title', {
                        ns: 'notifications'
                    }),
                    message: t('createdClient.message', {
                        ns: 'notifications'
                    })
                });
                onSave?.();
            },
            onError: (err: any) => {
                if (err?.error?.message === 'Error: Email currently in use by another user') {
                    notifications.show({
                        color: 'red',
                        title: t('primaryContactEmailAlreadyInUser.title', {
                            ns: 'notifications'
                        }),
                        message: t('primaryContactEmailAlreadyInUser.message', {
                            ns: 'notifications'
                        })
                    });
                } else {
                    notifications.show({
                        color: 'red',
                        title: t('errorCreatingClient.title', {
                            ns: 'notifications'
                        }),
                        message: t('errorCreatingClient.message', {
                            ns: 'notifications'
                        })
                    });
                }
            }
        });
        const onClickSave = React.useCallback(
            async (values: Record<string, any>) => {
                if (clientId) {
                    await patchClient.mutate({
                        orgId: clientId,
                        organization: values ?? {}
                    });
                } else {
                    await postClient.mutate({
                        client: values
                    });
                }
                // onSave?.();
                // return Promise.resolve();
            },
            [clientId, patchClient, postClient]
        );
        const onCancelEdit = React.useCallback(() => {
            if (FormRef?.current?.isDirty()) {
                openDiscardChangesModal({
                    confirmCallback: () => {
                        onCancel?.();
                        FormRef?.current?.reset();
                    }
                });
            } else {
                onCancel?.();
            }
        }, [FormRef]);

        React.useImperativeHandle(
            ref,
            () => ({
                onCancel: () => {
                    return onCancelEdit();
                }
            }),
            [onCancelEdit]
        );

        return (
            <>
                {!isNew && (loading || !client) ? (
                    <Spinner />
                ) : (
                    <FormScaffolding
                        ref={FormRef}
                        sections={manifestSections}
                        drawerCard
                        disableValidation={false}
                        ancillaryData={ancillaryData}
                        key={client?.ORG0002}
                        data={client}
                        onSave={async ({ values }) => {
                            await onClickSave(values);
                        }}
                        onCancel={onCancelEdit}
                        externalIsLoading={postClient?.isLoading || patchClient?.isLoading || loading}
                    />
                )}
            </>
        );
    }
);

export default ClientForm;
